const isVisible = element => {
  for (let node = element; node instanceof HTMLElement; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (node.hidden || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
  }
  return true;
};

export function mobilePriceParts(container) {
  if (!container || !isVisible(container)) return [];
  const source = [...container.children].find(child => child.hasAttribute('data-jp-priority-price') && isVisible(child)) ||
    [...container.children].find(child => child.hasAttribute('bss-b2b-product-price') && isVisible(child));
  if (!source) return [];
  const prices = [...source.querySelectorAll('.price, .compare-at-price')].filter(element =>
    isVisible(element) && !element.parentElement.closest('.price, .compare-at-price') && element.textContent.trim());
  const appPrices = prices.length ? prices : [...source.children].filter(element =>
    element.tagName === 'SPAN' && !element.children.length && isVisible(element) &&
    element.getAttribute('aria-hidden') !== 'true' && /\d/.test(element.textContent));
  const parts = appPrices.map(element => ({ text: element.textContent.trim(), compare:
    element.classList.contains('compare-at-price') ||
    `${getComputedStyle(element).textDecorationLine} ${getComputedStyle(element).textDecoration}`.includes('line-through') }));
  const tax = container.querySelector('.jp-price-tax-label');
  if (parts.length && tax && isVisible(tax)) parts.push({ text: tax.textContent.trim(), tax: true });
  return parts;
}

export class JpMobilePurchaseBar extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('.shopify-section')?.querySelector('.product-details');
    if (!this.root) return;
    this.price = this.querySelector('[data-mobile-price]');
    this.button = this.querySelector('[data-mobile-add]');
    this.media = matchMedia('(max-width: 749px)');
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.schedule = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    };
    this.media.addEventListener('change', this.schedule, { signal });
    this.button.addEventListener('click', () => this.add(), { signal });
    this.observer = new MutationObserver(this.schedule);
    this.observer.observe(this.root, { childList: true, subtree: true, characterData: true, attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'disabled', 'aria-disabled', 'data-adding', 'data-variant-change-pending', 'data-jp-price-pending', 'aria-busy'] });
    this.resize = new ResizeObserver(() => this.measure());
    this.resize.observe(this);
    this.update();
  }
  target() {
    const form = this.root.querySelector('product-form-component');
    return { form, button: form?.querySelector('button[ref="addToCartButton"]') };
  }
  update() {
    const { form, button } = this.target();
    const container = this.root.querySelector('product-price [ref="priceContainer"]');
    const parts = mobilePriceParts(container);
    const hidden = !this.media.matches || !form || !button || !isVisible(form) || !isVisible(button);
    if (this.hidden !== hidden) this.hidden = hidden;
    const key = JSON.stringify(parts);
    if (key !== this.priceKey) {
      this.price.replaceChildren(...parts.map(part => {
        const span = document.createElement('span');
        span.className = part.tax ? 'jp-price-tax-label' : part.compare ? 'compare-at-price' : 'price';
        span.textContent = part.text;
        return span;
      }));
      this.priceKey = key;
    }
    const disabled = !parts.length || !button || button.disabled || button.getAttribute('aria-disabled') === 'true' ||
      button.dataset.adding === 'true' || form?.dataset.variantChangePending === 'true' ||
      container?.hasAttribute('data-jp-price-pending') || container?.getAttribute('aria-busy') === 'true';
    if (this.button.disabled !== disabled) this.button.disabled = disabled;
    const text = button?.querySelector('.add-to-cart-text__content')?.textContent.trim();
    if (text && this.button.textContent !== text) this.button.textContent = text;
    if (this.followErrors) {
      const error = [...this.root.querySelectorAll('.product-form-text__error, .cl-po--error, [data-jp-bono-dimension-notice]')]
        .find(element => isVisible(element) && !element.classList.contains('hidden') && element.textContent.trim());
      if (error) {
        this.followErrors = false;
        error.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    }
    const radius = getComputedStyle(this.root).getPropertyValue('--jp-pdp-control-radius');
    if (this.style.getPropertyValue('--jp-pdp-control-radius') !== radius) this.style.setProperty('--jp-pdp-control-radius', radius);
    this.measure();
  }
  measure() {
    const height = this.hidden ? 0 : Math.ceil(this.getBoundingClientRect().height);
    if (this.height !== height) {
      this.height = height;
      document.documentElement.style.setProperty('--jp-mobile-purchase-height', `${height}px`);
    }
  }
  add() {
    this.update();
    if (this.button.disabled) return;
    const { button } = this.target();
    this.followErrors = true;
    button.click();
    this.update();
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.resize?.disconnect();
    cancelAnimationFrame(this.frame);
    document.documentElement.style.removeProperty('--jp-mobile-purchase-height');
  }
}

if (!customElements.get('jp-mobile-purchase-bar')) customElements.define('jp-mobile-purchase-bar', JpMobilePurchaseBar);
